import { db } from '../firebase';
import { 
  collection, 
  doc, 
  getDocs, 
  getDoc, 
  query, 
  orderBy,
  addDoc,
  setDoc,
  deleteDoc,
  where,
  collectionGroup
} from 'firebase/firestore';
import logger from '../utils/logger';

export const quizService = {

  // Get quizzes for a specific user
  async getUserQuizzes(userId) {
    logger.startTimer('getUserQuizzes');
    logger.apiCall(`getUserQuizzes/${userId}`, 'GET', { userId });
    
    try {
      logger.info(`Starting to fetch quizzes for user: ${userId}`, { userId }, 'QUIZ_SERVICE');
      
      // Use simple query first to avoid index issues
      const quizzesRef = collection(db, 'users', userId, 'quizzes');
      logger.debug('Created quizzes collection reference', { 
        userId, 
        path: `users/${userId}/quizzes` 
      }, 'QUIZ_SERVICE');
      
      // Get all quizzes without complex queries first
      const querySnapshot = await getDocs(quizzesRef);
      logger.info(`Raw query returned ${querySnapshot.size} documents`, { 
        userId, 
        docCount: querySnapshot.size,
        isEmpty: querySnapshot.empty 
      }, 'QUIZ_SERVICE');
      
      const allQuizzes = [];
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        allQuizzes.push({
          id: doc.id,
          userId: userId,
          ...data
        });
        
        logger.debug('Raw quiz document', { 
          quizId: doc.id,
          title: data.title,
          published: data.published,
          visibility: data.visibility,
          createdAt: data.createdAt
        }, 'QUIZ_SERVICE');
      });
      
      logger.info(`Found ${allQuizzes.length} total quizzes`, { 
        userId, 
        totalQuizzes: allQuizzes.length 
      }, 'QUIZ_SERVICE');
      
      // Return all quizzes for the user (including unpublished ones)
      const userQuizzes = allQuizzes.filter(quiz => {
        return true; // Show all quizzes for the user
      });
      
      logger.info(`User has ${userQuizzes.length} quizzes`, { 
        userId, 
        quizCount: userQuizzes.length 
      }, 'QUIZ_SERVICE');
      
      // Sort by creation date (client-side)
      userQuizzes.sort((a, b) => {
        const dateA = new Date(a.createdAt || 0);
        const dateB = new Date(b.createdAt || 0);
        return dateB - dateA; // desc order
      });
      
      const duration = logger.endTimer('getUserQuizzes');
      logger.info(`Successfully fetched ${userQuizzes.length} quizzes for user ${userId}`, { 
        userId,
        totalQuizzes: allQuizzes.length,
        userQuizzes: userQuizzes.length,
        duration,
        quizTitles: userQuizzes.map(q => q.title)
      }, 'QUIZ_SERVICE');
      
      return userQuizzes;
      
    } catch (error) {
      const duration = logger.endTimer('getUserQuizzes');
      logger.error(`Failed to fetch quizzes for user ${userId}`, {
        userId,
        duration,
        errorMessage: error.message,
        errorCode: error.code,
        errorStack: error.stack,
        firebaseError: {
          code: error.code,
          message: error.message,
          name: error.name
        }
      }, 'QUIZ_SERVICE');
      
      throw new Error(`クイズの取得に失敗しました: ${error.message}`);
    }
  },

  // Get quiz by ID
  async getQuiz(userId, quizId) {
    logger.startTimer('getQuiz');
    logger.apiCall(`getQuiz/${userId}/${quizId}`, 'GET', { userId, quizId });
    
    try {
      const quizRef = doc(db, 'users', userId, 'quizzes', quizId);
      const quizSnap = await getDoc(quizRef);
      
      if (quizSnap.exists()) {
        const quiz = {
          id: quizSnap.id,
          ...quizSnap.data()
        };
        
        const duration = logger.endTimer('getQuiz');
        logger.info(`Fetched quiz: ${quiz.title}`, { 
          quizId: quiz.id,
          title: quiz.title,
          duration 
        }, 'QUIZ_SERVICE');
        
        return quiz;
      } else {
        logger.warn(`Quiz not found: ${quizId}`, { userId, quizId }, 'QUIZ_SERVICE');
        throw new Error('Quiz not found');
      }
    } catch (error) {
      logger.apiError(`getQuiz/${userId}/${quizId}`, error, { userId, quizId });
      throw error;
    }
  },

  // Get questions for a quiz
  async getQuizQuestions(userId, quizId) {
    logger.startTimer('getQuizQuestions');
    logger.apiCall(`getQuizQuestions/${userId}/${quizId}`, 'GET', { userId, quizId });
    
    try {
      const questionsRef = collection(db, 'users', userId, 'quizzes', quizId, 'questions');
      const q = query(questionsRef, orderBy('order', 'asc'));
      
      const querySnapshot = await getDocs(q);
      const questions = [];
      
      querySnapshot.forEach((doc) => {
        questions.push({
          id: doc.id,
          ...doc.data()
        });
      });
      
      const duration = logger.endTimer('getQuizQuestions');
      logger.info(`Fetched ${questions.length} questions for quiz ${quizId}`, { 
        quizId,
        questionCount: questions.length,
        duration 
      }, 'QUIZ_SERVICE');
      
      return questions;
    } catch (error) {
      logger.apiError(`getQuizQuestions/${userId}/${quizId}`, error, { userId, quizId });
      throw error;
    }
  },

  // Shuffle array (for randomizing choice order)
  shuffleArray(array) {
    logger.debug(`Shuffling array of ${array.length} items`, { 
      originalLength: array.length 
    }, 'QUIZ_SERVICE');
    
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    
    logger.debug('Array shuffled successfully', { 
      shuffledLength: shuffled.length 
    }, 'QUIZ_SERVICE');
    
    return shuffled;
  },

  // Save quiz attempt
  async saveQuizAttempt(userId, quizId, attemptData) {
    logger.startTimer('saveQuizAttempt');
    logger.apiCall(`saveQuizAttempt/${userId}/${quizId}`, 'POST', { 
      userId, 
      quizId, 
      score: attemptData.score,
      answersCount: attemptData.answers.length 
    });
    
    try {
      const attemptsRef = collection(db, 'users', userId, 'quizzes', quizId, 'attempts');
      const docRef = await addDoc(attemptsRef, {
        ...attemptData,
        startedAt: new Date().toISOString(),
        finishedAt: new Date().toISOString()
      });
      
      const duration = logger.endTimer('saveQuizAttempt');
      logger.info(`Quiz attempt saved successfully`, { 
        attemptId: docRef.id,
        quizId,
        score: attemptData.score,
        totalQuestions: attemptData.answers.length,
        duration 
      }, 'QUIZ_SERVICE');
      
      return docRef.id;
    } catch (error) {
      logger.apiError(`saveQuizAttempt/${userId}/${quizId}`, error, { 
        userId, 
        quizId, 
        attemptData 
      });
      throw error;
    }
  },

  // Get quiz attempts for a user
  async getQuizAttempts(userId, quizId) {
    logger.startTimer('getQuizAttempts');
    logger.apiCall(`getQuizAttempts/${userId}/${quizId}`, 'GET', { userId, quizId });
    
    try {
      const attemptsRef = collection(db, 'users', userId, 'quizzes', quizId, 'attempts');
      const q = query(attemptsRef, orderBy('startedAt', 'desc'));
      
      const querySnapshot = await getDocs(q);
      const attempts = [];
      
      querySnapshot.forEach((doc) => {
        attempts.push({
          id: doc.id,
          ...doc.data()
        });
      });
      
      const duration = logger.endTimer('getQuizAttempts');
      logger.info(`Fetched ${attempts.length} attempts for quiz ${quizId}`, { 
        quizId,
        attemptCount: attempts.length,
        duration 
      }, 'QUIZ_SERVICE');
      
      return attempts;
    } catch (error) {
      logger.apiError(`getQuizAttempts/${userId}/${quizId}`, error, { userId, quizId });
      throw error;
    }
  },

  // Get answered questions from attempts
  async getAnsweredQuestions(userId, quizId) {
    logger.startTimer('getAnsweredQuestions');
    logger.apiCall(`getAnsweredQuestions/${userId}/${quizId}`, 'GET', { userId, quizId });
    
    try {
      const attempts = await this.getQuizAttempts(userId, quizId);
      
      const answeredQuestionIds = new Set();
      attempts.forEach(attempt => {
        if (attempt.answers && Array.isArray(attempt.answers)) {
          attempt.answers.forEach(answer => {
            if (answer.questionId) {
              answeredQuestionIds.add(answer.questionId);
            }
          });
        }
      });
      
      const answeredQuestions = Array.from(answeredQuestionIds);
      const duration = logger.endTimer('getAnsweredQuestions');
      logger.info(`Found ${answeredQuestions.length} answered questions for quiz ${quizId}`, { 
        quizId,
        answeredQuestionCount: answeredQuestions.length,
        answeredQuestions,
        duration 
      }, 'QUIZ_SERVICE');
      
      return answeredQuestions;
    } catch (error) {
      logger.apiError(`getAnsweredQuestions/${userId}/${quizId}`, error, { userId, quizId });
      throw error;
    }
  },

  // Get incorrectly answered questions from attempts
  async getIncorrectQuestions(userId, quizId) {
    logger.startTimer('getIncorrectQuestions');
    logger.apiCall(`getIncorrectQuestions/${userId}/${quizId}`, 'GET', { userId, quizId });
    
    try {
      const attempts = await this.getQuizAttempts(userId, quizId);
      
      // Sort attempts by timestamp (newest first) to prioritize latest answers
      const sortedAttempts = attempts.sort((a, b) => {
        const timeA = new Date(a.finishedAt || a.startedAt);
        const timeB = new Date(b.finishedAt || b.startedAt);
        return timeB - timeA;
      });
      
      const questionAnswerStatus = new Map(); // questionId -> isCorrect (latest answer)
      
      // Process attempts from newest to oldest, keeping only the latest answer for each question
      sortedAttempts.forEach(attempt => {
        if (attempt.answers && Array.isArray(attempt.answers)) {
          attempt.answers.forEach(answer => {
            if (answer.questionId && typeof answer.isCorrect === 'boolean') {
              // Only set if this question hasn't been processed yet (newest answer wins)
              if (!questionAnswerStatus.has(answer.questionId)) {
                questionAnswerStatus.set(answer.questionId, answer.isCorrect);
              }
            }
          });
        }
      });
      
      // Get only questions that were answered incorrectly in their latest attempt
      const incorrectQuestions = Array.from(questionAnswerStatus.entries())
        .filter(([questionId, isCorrect]) => !isCorrect)
        .map(([questionId, isCorrect]) => questionId);
      const duration = logger.endTimer('getIncorrectQuestions');
      logger.info(`Found ${incorrectQuestions.length} incorrect questions for quiz ${quizId} (based on latest answers)`, { 
        quizId,
        totalAttempts: attempts.length,
        incorrectQuestionCount: incorrectQuestions.length,
        incorrectQuestions,
        duration 
      }, 'QUIZ_SERVICE');
      
      return incorrectQuestions;
    } catch (error) {
      logger.apiError(`getIncorrectQuestions/${userId}/${quizId}`, error, { userId, quizId });
      throw error;
    }
  },

  // Get question presentation count from attempts
  async getQuestionPresentationCount(userId, quizId) {
    logger.startTimer('getQuestionPresentationCount');
    logger.apiCall(`getQuestionPresentationCount/${userId}/${quizId}`, 'GET', { userId, quizId });
    
    try {
      const attempts = await this.getQuizAttempts(userId, quizId);
      
      const presentationCount = new Map();
      
      attempts.forEach(attempt => {
        if (attempt.answers && Array.isArray(attempt.answers)) {
          attempt.answers.forEach(answer => {
            if (answer.questionId) {
              const currentCount = presentationCount.get(answer.questionId) || 0;
              presentationCount.set(answer.questionId, currentCount + 1);
            }
          });
        }
      });
      
      const duration = logger.endTimer('getQuestionPresentationCount');
      logger.info(`Question presentation count calculated for quiz ${quizId}`, { 
        quizId,
        totalQuestions: presentationCount.size,
        duration,
        presentationData: Object.fromEntries(presentationCount)
      }, 'QUIZ_SERVICE');
      
      return presentationCount;
    } catch (error) {
      logger.apiError(`getQuestionPresentationCount/${userId}/${quizId}`, error, { userId, quizId });
      throw error;
    }
  },

  // Select questions with presentation count consideration
  async selectQuestionsWithPresentationCount(userId, quizId, allQuestions, settings) {
    logger.startTimer('selectQuestionsWithPresentationCount');
    logger.info(`Selecting questions with presentation count for quiz ${quizId}`, { 
      quizId,
      totalQuestions: allQuestions.length,
      settings
    }, 'QUIZ_SERVICE');
    
    try {
      const presentationCount = await this.getQuestionPresentationCount(userId, quizId);
      
      // Add presentation count to questions
      const questionsWithCount = allQuestions.map(question => ({
        ...question,
        presentationCount: presentationCount.get(question.id) || 0
      }));
      
      let selectedQuestions = [...questionsWithCount];
      
      // Apply ordering
      if (settings.orderMode === 'random') {
        selectedQuestions = this.shuffleArray(selectedQuestions);
      } else {
        // Keep original order (sorted by order field)
        selectedQuestions.sort((a, b) => a.order - b.order);
        
        // Handle sequential ordering options
        if (settings.continueMode === true) {
          // Continue from unanswered questions
          const answeredQuestionIds = settings.answeredQuestions || [];
          const unansweredQuestions = selectedQuestions.filter(q => q?.id && !answeredQuestionIds.includes(q.id));
          
          if (unansweredQuestions.length === 0) {
            // No unanswered questions, select questions with least presentation count
            logger.info('No unanswered questions found, selecting questions with least presentation count', { 
              quizId,
              totalQuestions: selectedQuestions.length
            }, 'QUIZ_SERVICE');
            
            // Find minimum presentation count
            const minPresentationCount = Math.min(...selectedQuestions.map(q => q.presentationCount));
            const leastPresentedQuestions = selectedQuestions.filter(q => q.presentationCount === minPresentationCount);
            
            selectedQuestions = leastPresentedQuestions;
            
            logger.info(`Selected ${selectedQuestions.length} questions with minimum presentation count of ${minPresentationCount}`, { 
              quizId,
              minPresentationCount,
              selectedCount: selectedQuestions.length
            }, 'QUIZ_SERVICE');
          } else {
            selectedQuestions = unansweredQuestions;
          }
        } else if (settings.continueMode === 'incorrect') {
          // Show only incorrect questions
          const incorrectQuestionIds = settings.incorrectQuestions || [];
          selectedQuestions = selectedQuestions.filter(q => q?.id && incorrectQuestionIds.includes(q.id));
        } else if (settings.startFromQuestion && settings.startFromQuestion > 1) {
          // Start from specified question number
          const startIndex = settings.startFromQuestion - 1;
          selectedQuestions = selectedQuestions.slice(startIndex);
        }
      }
      
      // Select specified number of questions
      const questionCount = Math.min(settings.questionCount, selectedQuestions.length);
      selectedQuestions = selectedQuestions.slice(0, questionCount);
      
      const duration = logger.endTimer('selectQuestionsWithPresentationCount');
      logger.info(`Selected ${selectedQuestions.length} questions for quiz ${quizId}`, { 
        quizId,
        selectedCount: selectedQuestions.length,
        requestedCount: settings.questionCount,
        duration,
        selectedQuestionIds: selectedQuestions.map(q => q.id)
      }, 'QUIZ_SERVICE');
      
      return selectedQuestions;
    } catch (error) {
      logger.apiError(`selectQuestionsWithPresentationCount/${userId}/${quizId}`, error, { userId, quizId });
      throw error;
    }
  },

  // Calculate quiz score
  calculateScore(answers) {
    logger.debug(`Calculating score for ${answers.length} answers`, { 
      totalAnswers: answers.length 
    }, 'QUIZ_SERVICE');
    
    const score = answers.reduce((total, answer) => {
      return total + (answer.isCorrect ? 1 : 0);
    }, 0);
    
    const percentage = Math.round((score / answers.length) * 100);
    
    logger.info(`Score calculated: ${score}/${answers.length} (${percentage}%)`, { 
      score,
      totalQuestions: answers.length,
      percentage,
      correctAnswers: answers.filter(a => a.isCorrect).length
    }, 'QUIZ_SERVICE');
    
    return score;
  },

  // Add quiz to favorites (using shared_quizzes)
  async addToFavorites(userId, quizOwnerId, quizId) {
    logger.startTimer('addToFavorites');
    logger.apiCall(`addToFavorites/${userId}/${quizOwnerId}/${quizId}`, 'POST', { userId, quizOwnerId, quizId });
    
    try {
      const sharedQuizRef = doc(db, 'users', userId, 'shared_quizzes', quizId);
      await setDoc(sharedQuizRef, {
        originalUserId: quizOwnerId,
        isFavorite: true,
        addedAt: new Date().toISOString()
      }, { merge: true });
      
      const duration = logger.endTimer('addToFavorites');
      logger.info(`Added quiz to favorites successfully`, { 
        userId,
        quizOwnerId,
        quizId,
        sharedQuizId: quizId,
        duration 
      }, 'QUIZ_SERVICE');
      
    } catch (error) {
      const duration = logger.endTimer('addToFavorites');
      logger.error(`Failed to add quiz to favorites`, {
        userId,
        quizOwnerId,
        quizId,
        duration,
        errorMessage: error.message,
        errorCode: error.code
      }, 'QUIZ_SERVICE');
      throw new Error(`お気に入りの追加に失敗しました: ${error.message}`);
    }
  },

  // Remove quiz from favorites (using shared_quizzes)
  async removeFromFavorites(userId, quizOwnerId, quizId) {
    logger.startTimer('removeFromFavorites');
    logger.apiCall(`removeFromFavorites/${userId}/${quizOwnerId}/${quizId}`, 'DELETE', { userId, quizOwnerId, quizId });
    
    try {
      const sharedQuizRef = doc(db, 'users', userId, 'shared_quizzes', quizId);
      await setDoc(sharedQuizRef, {
        isFavorite: false
      }, { merge: true });
      
      const duration = logger.endTimer('removeFromFavorites');
      logger.info(`Removed quiz from favorites successfully`, { 
        userId,
        quizOwnerId,
        quizId,
        sharedQuizId: quizId,
        duration 
      }, 'QUIZ_SERVICE');
      
    } catch (error) {
      const duration = logger.endTimer('removeFromFavorites');
      logger.error(`Failed to remove quiz from favorites`, {
        userId,
        quizOwnerId,
        quizId,
        duration,
        errorMessage: error.message,
        errorCode: error.code
      }, 'QUIZ_SERVICE');
      throw new Error(`お気に入りの削除に失敗しました: ${error.message}`);
    }
  },

  // Check if quiz is in favorites (using shared_quizzes)
  async isQuizFavorited(userId, quizOwnerId, quizId) {
    logger.startTimer('isQuizFavorited');
    logger.apiCall(`isQuizFavorited/${userId}/${quizOwnerId}/${quizId}`, 'GET', { userId, quizOwnerId, quizId });
    
    try {
      const sharedQuizRef = doc(db, 'users', userId, 'shared_quizzes', quizId);
      const sharedQuizSnap = await getDoc(sharedQuizRef);
      
      const isFavorited = sharedQuizSnap.exists() && sharedQuizSnap.data().isFavorite === true;
      const duration = logger.endTimer('isQuizFavorited');
      logger.info(`Checked if quiz is favorited: ${isFavorited}`, { 
        userId,
        quizOwnerId,
        quizId,
        isFavorited,
        duration 
      }, 'QUIZ_SERVICE');
      
      return isFavorited;
    } catch (error) {
      const duration = logger.endTimer('isQuizFavorited');
      logger.error(`Failed to check if quiz is favorited`, {
        userId,
        quizOwnerId,
        quizId,
        duration,
        errorMessage: error.message,
        errorCode: error.code
      }, 'QUIZ_SERVICE');
      throw new Error(`お気に入り状態の確認に失敗しました: ${error.message}`);
    }
  },

  // Get user's favorite quizzes
  // - Own quizzes marked isFavorite=true under users/{userId}/quizzes
  // - Public quizzes from others favorited under users/{userId}/shared_quizzes with isFavorite=true
  async getFavoriteQuizzes(userId) {
    logger.startTimer('getFavoriteQuizzes');
    logger.apiCall(`getFavoriteQuizzes/${userId}`, 'GET', { userId });
    
    try {
      const favoriteQuizzes = [];

      // 1) Own quizzes with isFavorite=true
      try {
        const ownQuizzesRef = collection(db, 'users', userId, 'quizzes');
        const ownFavQuery = query(ownQuizzesRef, where('isFavorite', '==', true));
        const ownFavSnapshot = await getDocs(ownFavQuery);
        ownFavSnapshot.forEach((docSnap) => {
          const data = docSnap.data();
          favoriteQuizzes.push({
            id: docSnap.id,
            userId: userId,
            ...data,
            isFavorite: true
          });
        });
      } catch (ownError) {
        logger.warn('Failed to fetch own favorite quizzes', { userId, errorMessage: ownError.message }, 'QUIZ_SERVICE');
      }

      // 2) Shared/public quizzes favorited via shared_quizzes
      const sharedQuizzesRef = collection(db, 'users', userId, 'shared_quizzes');
      const sharedQuizzesQuery = query(sharedQuizzesRef, where('isFavorite', '==', true));
      const sharedQuizzesSnapshot = await getDocs(sharedQuizzesQuery);
      
      for (const sharedQuizDoc of sharedQuizzesSnapshot.docs) {
        const sharedQuizData = sharedQuizDoc.data();
        const { originalUserId } = sharedQuizData;
        const quizId = sharedQuizDoc.id; // Document ID is the quizId
        
        try {
          // Get the actual quiz data
          const quizRef = doc(db, 'users', originalUserId, 'quizzes', quizId);
          const quizSnap = await getDoc(quizRef);
          
          if (quizSnap.exists()) {
            const quizData = quizSnap.data();
            
            // Only include public quizzes
            if (quizData.visibility === 'public' && quizData.published) {
              // id / userId はパスから決定した値を優先する（他ユーザーが書き込んだフィールドで作成者を偽装させない）
              favoriteQuizzes.push({
                ...quizData,
                id: quizId,
                userId: originalUserId,
                sharedQuizId: sharedQuizDoc.id,
                addedAt: sharedQuizData.addedAt
              });
            }
          } else {
            // Quiz doesn't exist anymore, remove favorite flag
            logger.warn(`Quiz not found, removing favorite flag`, { 
              userId,
              quizId,
              originalUserId,
              sharedQuizId: sharedQuizDoc.id
            }, 'QUIZ_SERVICE');
            
            await setDoc(doc(db, 'users', userId, 'shared_quizzes', quizId), {
              isFavorite: false
            }, { merge: true });
          }
        } catch (error) {
          logger.warn(`Failed to fetch quiz data for favorite`, {
            userId,
            quizId,
            originalUserId,
            sharedQuizId: sharedQuizDoc.id,
            errorMessage: error.message
          }, 'QUIZ_SERVICE');
        }
      }
      
      // Sort: prefer addedAt (shared) then createdAt (own)
      favoriteQuizzes.sort((a, b) => {
        const da = new Date(a.addedAt || a.createdAt || 0);
        const db = new Date(b.addedAt || b.createdAt || 0);
        return db - da;
      });

      const duration = logger.endTimer('getFavoriteQuizzes');
      logger.info(`Fetched ${favoriteQuizzes.length} favorite quizzes for user ${userId}`, { 
        userId,
        favoriteCount: favoriteQuizzes.length,
        duration,
        quizTitles: favoriteQuizzes.map(q => q.title)
      }, 'QUIZ_SERVICE');
      
      return favoriteQuizzes;
    } catch (error) {
      const duration = logger.endTimer('getFavoriteQuizzes');
      logger.error(`Failed to fetch favorite quizzes for user ${userId}`, {
        userId,
        duration,
        errorMessage: error.message,
        errorCode: error.code
      }, 'QUIZ_SERVICE');
      throw new Error(`お気に入りクイズの取得に失敗しました: ${error.message}`);
    }
  },

  // Get public quizzes from all users
  async getPublicQuizzes() {
    logger.startTimer('getPublicQuizzes');
    logger.apiCall('getPublicQuizzes', 'GET', {});
    
    try {
      logger.info('Starting to fetch public quizzes', {}, 'QUIZ_SERVICE');
      
      // Use collectionGroup to query across all users
      const quizzesRef = collectionGroup(db, 'quizzes');
      logger.debug('Created collection group reference for quizzes', {}, 'QUIZ_SERVICE');
      
      // First try with orderBy, fallback to without orderBy if index is not ready
      let publicQuizzesQuery, querySnapshot;
      
      try {
        publicQuizzesQuery = query(
          quizzesRef,
          where('visibility', '==', 'public'),
          where('published', '==', true),
          orderBy('createdAt', 'desc')
        );
        logger.debug('Created query for public quizzes with orderBy', { 
          conditions: ['visibility==public', 'published==true'],
          orderBy: 'createdAt desc'
        }, 'QUIZ_SERVICE');
        
        querySnapshot = await getDocs(publicQuizzesQuery);
      } catch (indexError) {
        if (indexError.code === 'failed-precondition' || indexError.message.includes('index')) {
          logger.warn('Index not ready, falling back to query without orderBy', {
            indexError: indexError.message
          }, 'QUIZ_SERVICE');
          
          // Fallback query without orderBy
          publicQuizzesQuery = query(
            quizzesRef,
            where('visibility', '==', 'public'),
            where('published', '==', true)
          );
          
          querySnapshot = await getDocs(publicQuizzesQuery);
        } else {
          throw indexError;
        }
      }
      
      logger.info(`Public quizzes query returned ${querySnapshot.size} documents`, { 
        docCount: querySnapshot.size,
        isEmpty: querySnapshot.empty 
      }, 'QUIZ_SERVICE');
      
      const publicQuizzes = [];
      
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        // Extract userId from document path: users/{userId}/quizzes/{quizId}
        const pathSegments = doc.ref.path.split('/');
        const userId = pathSegments[1];
        
        logger.debug('Processing public quiz document', { 
          quizId: doc.id,
          userId: userId,
          title: data.title,
          visibility: data.visibility,
          published: data.published,
          pathSegments: pathSegments
        }, 'QUIZ_SERVICE');
        
        // id / userId はパスから決定した値を優先する（他ユーザーが書き込んだフィールドで作成者を偽装させない）
        publicQuizzes.push({
          ...data,
          id: doc.id,
          userId: userId
        });
      });
      
      // Sort by creation date on client side if we used the fallback query
      publicQuizzes.sort((a, b) => {
        const dateA = new Date(a.createdAt || 0);
        const dateB = new Date(b.createdAt || 0);
        return dateB - dateA; // desc order
      });
      
      const duration = logger.endTimer('getPublicQuizzes');
      logger.info(`Fetched ${publicQuizzes.length} public quizzes`, { 
        publicQuizCount: publicQuizzes.length,
        duration,
        quizTitles: publicQuizzes.map(q => q.title)
      }, 'QUIZ_SERVICE');
      
      return publicQuizzes;
    } catch (error) {
      const duration = logger.endTimer('getPublicQuizzes');
      logger.error(`Failed to fetch public quizzes`, {
        duration,
        errorMessage: error.message,
        errorCode: error.code,
        errorStack: error.stack,
        firebaseError: {
          code: error.code,
          message: error.message,
          name: error.name
        }
      }, 'QUIZ_SERVICE');
      throw new Error(`公開クイズの取得に失敗しました: ${error.message}`);
    }
  },

  // Save attempt for shared/public quiz
  async saveSharedQuizAttempt(userId, originalUserId, originalQuizId, attemptData) {
    logger.startTimer('saveSharedQuizAttempt');
    logger.apiCall(`saveSharedQuizAttempt/${userId}/${originalUserId}/${originalQuizId}`, 'POST', { 
      userId, 
      originalUserId,
      originalQuizId,
      score: attemptData.score 
    });
    
    try {
      // Create or update the shared_quizzes document first
      const sharedQuizRef = doc(db, `users/${userId}/shared_quizzes/${originalQuizId}`);
      await setDoc(sharedQuizRef, {
        originalUserId: originalUserId
      }, { merge: true });
      
      logger.debug('Created/updated shared quiz reference', {
        userId,
        originalUserId,
        originalQuizId
      }, 'QUIZ_SERVICE');
      
      // Save the attempt
      const attemptsRef = collection(db, `users/${userId}/shared_quizzes/${originalQuizId}/attempts`);
      const attemptRef = await addDoc(attemptsRef, {
        userId: attemptData.userId,
        originalQuizId: originalQuizId,
        originalUserId: originalUserId,
        startedAt: attemptData.startedAt,
        finishedAt: attemptData.finishedAt,
        answers: attemptData.answers,
        score: attemptData.score
      });
      
      const duration = logger.endTimer('saveSharedQuizAttempt');
      logger.info(`Saved shared quiz attempt`, {
        userId,
        originalUserId,
        originalQuizId,
        attemptId: attemptRef.id,
        score: attemptData.score,
        duration
      }, 'QUIZ_SERVICE');
      
      return attemptRef.id;
      
    } catch (error) {
      const duration = logger.endTimer('saveSharedQuizAttempt');
      logger.apiError(`saveSharedQuizAttempt/${userId}/${originalUserId}/${originalQuizId}`, error, { 
        userId,
        originalUserId, 
        originalQuizId,
        duration,
        errorMessage: error.message,
        errorCode: error.code
      });
      throw new Error(`公開クイズの回答履歴保存に失敗しました: ${error.message}`);
    }
  },

  // Get shared quiz attempts for a specific quiz
  async getSharedQuizAttemptsForQuiz(userId, quizId) {
    logger.startTimer('getSharedQuizAttemptsForQuiz');
    logger.apiCall(`getSharedQuizAttemptsForQuiz/${userId}/${quizId}`, 'GET', { userId, quizId });
    
    try {
      const attemptsRef = collection(db, `users/${userId}/shared_quizzes/${quizId}/attempts`);
      const attemptsQuery = query(attemptsRef, orderBy('finishedAt', 'desc'));
      const attemptsSnapshot = await getDocs(attemptsQuery);
      
      const attempts = [];
      attemptsSnapshot.forEach((attemptDoc) => {
        const attemptData = attemptDoc.data();
        attempts.push({
          id: attemptDoc.id,
          ...attemptData
        });
      });
      
      const duration = logger.endTimer('getSharedQuizAttemptsForQuiz');
      logger.info(`Fetched ${attempts.length} shared quiz attempts for quiz ${quizId}`, {
        userId,
        quizId,
        attemptCount: attempts.length,
        duration
      }, 'QUIZ_SERVICE');
      
      return attempts;
      
    } catch (error) {
      const duration = logger.endTimer('getSharedQuizAttemptsForQuiz');
      logger.error(`Failed to fetch shared quiz attempts for quiz ${quizId}`, {
        userId,
        quizId,
        duration,
        errorMessage: error.message,
        errorCode: error.code
      }, 'QUIZ_SERVICE');
      throw new Error(`公開クイズの回答履歴取得に失敗しました: ${error.message}`);
    }
  },

  // Get answered questions from shared quiz attempts
  async getSharedQuizAnsweredQuestions(userId, quizId) {
    logger.startTimer('getSharedQuizAnsweredQuestions');
    logger.apiCall(`getSharedQuizAnsweredQuestions/${userId}/${quizId}`, 'GET', { userId, quizId });
    
    try {
      const attempts = await this.getSharedQuizAttemptsForQuiz(userId, quizId);
      
      const answeredQuestionIds = new Set();
      attempts.forEach(attempt => {
        if (attempt.answers && Array.isArray(attempt.answers)) {
          attempt.answers.forEach(answer => {
            if (answer.questionId) {
              answeredQuestionIds.add(answer.questionId);
            }
          });
        }
      });
      
      const answeredQuestions = Array.from(answeredQuestionIds);
      const duration = logger.endTimer('getSharedQuizAnsweredQuestions');
      logger.info(`Found ${answeredQuestions.length} answered questions for shared quiz ${quizId}`, { 
        userId,
        quizId,
        answeredQuestionCount: answeredQuestions.length,
        answeredQuestions,
        duration 
      }, 'QUIZ_SERVICE');
      
      return answeredQuestions;
    } catch (error) {
      logger.apiError(`getSharedQuizAnsweredQuestions/${userId}/${quizId}`, error, { userId, quizId });
      throw error;
    }
  },

  // Get incorrectly answered questions from shared quiz attempts
  async getSharedQuizIncorrectQuestions(userId, quizId) {
    logger.startTimer('getSharedQuizIncorrectQuestions');
    logger.apiCall(`getSharedQuizIncorrectQuestions/${userId}/${quizId}`, 'GET', { userId, quizId });
    
    try {
      const attempts = await this.getSharedQuizAttemptsForQuiz(userId, quizId);
      
      // Sort attempts by timestamp (newest first) to prioritize latest answers
      const sortedAttempts = attempts.sort((a, b) => {
        const timeA = new Date(a.finishedAt || a.startedAt);
        const timeB = new Date(b.finishedAt || b.startedAt);
        return timeB - timeA;
      });
      
      const questionAnswerStatus = new Map(); // questionId -> isCorrect (latest answer)
      
      // Process attempts from newest to oldest, keeping only the latest answer for each question
      sortedAttempts.forEach(attempt => {
        if (attempt.answers && Array.isArray(attempt.answers)) {
          attempt.answers.forEach(answer => {
            if (answer.questionId && typeof answer.isCorrect === 'boolean') {
              // Only set if this question hasn't been processed yet (newest answer wins)
              if (!questionAnswerStatus.has(answer.questionId)) {
                questionAnswerStatus.set(answer.questionId, answer.isCorrect);
              }
            }
          });
        }
      });
      
      // Get only questions that were answered incorrectly in their latest attempt
      const incorrectQuestions = Array.from(questionAnswerStatus.entries())
        .filter(([questionId, isCorrect]) => !isCorrect)
        .map(([questionId, isCorrect]) => questionId);
        
      const duration = logger.endTimer('getSharedQuizIncorrectQuestions');
      logger.info(`Found ${incorrectQuestions.length} incorrect questions for shared quiz ${quizId} (based on latest answers)`, { 
        userId,
        quizId,
        totalAttempts: attempts.length,
        incorrectQuestionCount: incorrectQuestions.length,
        incorrectQuestions,
        duration 
      }, 'QUIZ_SERVICE');
      
      return incorrectQuestions;
    } catch (error) {
      logger.apiError(`getSharedQuizIncorrectQuestions/${userId}/${quizId}`, error, { userId, quizId });
      throw error;
    }
  },

  // Get user's shared quiz attempts
  async getSharedQuizAttempts(userId) {
    logger.startTimer('getSharedQuizAttempts');
    logger.apiCall(`getSharedQuizAttempts/${userId}`, 'GET', { userId });
    
    try {
      const sharedQuizzesRef = collection(db, `users/${userId}/shared_quizzes`);
      const sharedQuizzesSnapshot = await getDocs(sharedQuizzesRef);
      
      const sharedQuizAttempts = [];
      
      for (const sharedQuizDoc of sharedQuizzesSnapshot.docs) {
        const sharedQuizData = sharedQuizDoc.data();
        const quizId = sharedQuizDoc.id;
        const originalUserId = sharedQuizData.originalUserId;
        
        // Get attempts for this shared quiz
        const attemptsRef = collection(db, `users/${userId}/shared_quizzes/${quizId}/attempts`);
        const attemptsQuery = query(attemptsRef, orderBy('finishedAt', 'desc'));
        const attemptsSnapshot = await getDocs(attemptsQuery);
        
        // Get original quiz info
        try {
          const originalQuizRef = doc(db, `users/${originalUserId}/quizzes/${quizId}`);
          const originalQuizDoc = await getDoc(originalQuizRef);
          
          if (originalQuizDoc.exists()) {
            const originalQuizData = originalQuizDoc.data();
            
            attemptsSnapshot.forEach((attemptDoc) => {
              const attemptData = attemptDoc.data();
              sharedQuizAttempts.push({
                id: attemptDoc.id,
                quizId: quizId,
                originalUserId: originalUserId,
                quizTitle: originalQuizData.title,
                quizDescription: originalQuizData.description,
                ...attemptData
              });
            });
          }
        } catch (originalQuizError) {
          logger.warn('Failed to fetch original quiz data', {
            userId,
            quizId,
            originalUserId,
            error: originalQuizError.message
          }, 'QUIZ_SERVICE');
        }
      }
      
      // Sort by finished date
      sharedQuizAttempts.sort((a, b) => new Date(b.finishedAt) - new Date(a.finishedAt));
      
      const duration = logger.endTimer('getSharedQuizAttempts');
      logger.info(`Fetched ${sharedQuizAttempts.length} shared quiz attempts for user ${userId}`, {
        userId,
        attemptCount: sharedQuizAttempts.length,
        duration
      }, 'QUIZ_SERVICE');
      
      return sharedQuizAttempts;
      
    } catch (error) {
      const duration = logger.endTimer('getSharedQuizAttempts');
      logger.error(`Failed to fetch shared quiz attempts for user ${userId}`, {
        userId,
        duration,
        errorMessage: error.message,
        errorCode: error.code
      }, 'QUIZ_SERVICE');
      throw new Error(`公開クイズの回答履歴取得に失敗しました: ${error.message}`);
    }
  },

  // Toggle favorite flag for own quiz
  async toggleOwnQuizFavorite(userId, quizId) {
    logger.startTimer('toggleOwnQuizFavorite');
    logger.apiCall(`toggleOwnQuizFavorite/${userId}/${quizId}`, 'PUT', { userId, quizId });
    
    try {
      const quizRef = doc(db, `users/${userId}/quizzes/${quizId}`);
      const quizSnap = await getDoc(quizRef);
      
      if (!quizSnap.exists()) {
        throw new Error('クイズが見つかりません');
      }
      
      const currentData = quizSnap.data();
      const newFavoriteState = !currentData.isFavorite;
      
      await setDoc(quizRef, { isFavorite: newFavoriteState }, { merge: true });
      
      const duration = logger.endTimer('toggleOwnQuizFavorite');
      logger.info(`Toggled own quiz favorite: ${newFavoriteState}`, { 
        userId,
        quizId,
        newFavoriteState,
        duration 
      }, 'QUIZ_SERVICE');
      
      return newFavoriteState;
    } catch (error) {
      const duration = logger.endTimer('toggleOwnQuizFavorite');
      logger.error(`Failed to toggle own quiz favorite`, {
        userId,
        quizId,
        duration,
        errorMessage: error.message,
        errorCode: error.code
      }, 'QUIZ_SERVICE');
      throw new Error(`お気に入りの更新に失敗しました: ${error.message}`);
    }
  },

  // Toggle favorite flag for shared quiz
  async toggleSharedQuizFavorite(userId, sharedQuizId) {
    logger.startTimer('toggleSharedQuizFavorite');
    logger.apiCall(`toggleSharedQuizFavorite/${userId}/${sharedQuizId}`, 'PUT', { userId, sharedQuizId });
    
    try {
      const sharedQuizRef = doc(db, `users/${userId}/shared_quizzes/${sharedQuizId}`);
      const sharedQuizSnap = await getDoc(sharedQuizRef);
      
      if (!sharedQuizSnap.exists()) {
        // If shared_quizzes document doesn't exist, create it first
        await setDoc(sharedQuizRef, { 
          isFavorite: true,
          originalUserId: '' // This should be set when first accessing the shared quiz
        });
        
        const duration = logger.endTimer('toggleSharedQuizFavorite');
        logger.info(`Created and favorited shared quiz: true`, { 
          userId,
          sharedQuizId,
          newFavoriteState: true,
          duration 
        }, 'QUIZ_SERVICE');
        
        return true;
      }
      
      const currentData = sharedQuizSnap.data();
      const newFavoriteState = !currentData.isFavorite;
      
      await setDoc(sharedQuizRef, { isFavorite: newFavoriteState }, { merge: true });
      
      const duration = logger.endTimer('toggleSharedQuizFavorite');
      logger.info(`Toggled shared quiz favorite: ${newFavoriteState}`, { 
        userId,
        sharedQuizId,
        newFavoriteState,
        duration 
      }, 'QUIZ_SERVICE');
      
      return newFavoriteState;
    } catch (error) {
      const duration = logger.endTimer('toggleSharedQuizFavorite');
      logger.error(`Failed to toggle shared quiz favorite`, {
        userId,
        sharedQuizId,
        duration,
        errorMessage: error.message,
        errorCode: error.code
      }, 'QUIZ_SERVICE');
      throw new Error(`お気に入りの更新に失敗しました: ${error.message}`);
    }
  }
};
